import { RegionApi } from '@business/api/tm_region';
import { CategoryId } from '@business/entities/tm';
import { RegionId, TmRegionResponse } from '@business/entities/tm/TmRegionResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { CategoryRegionProviderPriorityForm } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/CategoryTab/ManageProviderPriorityOverlay/ManageProviderPriorityOverlay';
import { RegionFormData } from '@modules/settings/SettingsPage/Components/TimeMaterialsTab/AddEditRegionModal/RegionForm';
import { switchMap, tap } from 'rxjs/operators';

export class RegionBloc extends BaseBloc {
  private readonly tmRegionsRepo = new Repository<TmRegionResponse>({
    getItemId: term => term.id,
  });

  readonly regions$ = this.tmRegionsRepo.items$;
  readonly selectTemplate = this.tmRegionsRepo.selectItem;

  onReset() {
    this.tmRegionsRepo.reset();
  }

  fetchRegions = () => {
    return RegionApi.fetchRegions().pipe(this.tmRegionsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_region_error') }));
  };

  fetchCategoryServiceProviders = (categoryId: CategoryId) => {
    return RegionApi.fetchCategoryServiceProviders(categoryId).pipe(
      this.tmRegionsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_region_error') }),
    );
  };

  fetchRegion = (id: RegionId) => {
    return RegionApi.fetchRegion(id).pipe(
      this.tmRegionsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_region_error') }),
    );
  };

  createRegion = (data: RegionFormData) => {
    return RegionApi.createRegion(data).pipe(
      this.tmRegionsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_region'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  updateRegion = (id: RegionId, data: RegionFormData) => {
    return askChangeReason().pipe(
      switchMap(changeNote => RegionApi.updateRegion(id, data, changeNote)),
      this.tmRegionsRepo.ops.upsertOne(item => ({ item })),
      tap(() => this.fetchRegions().subscribe()),
      handleMessage({
        type: requestMessage('update_region'),
      }),
    );
  };

  deleteRegion = (id: RegionId) => {
    return askChangeReason('Delete Region', 'Do you really want to remove this region?', 'Delete').pipe(
      switchMap(changeNote => RegionApi.deleteRegion(id, changeNote)),
      this.tmRegionsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_region'),
      }),
    );
  };

  // Asks for the reason first (askChangeReason, kept in the Activity Log); closing that window saves nothing
  saveProviderPriorities = (data: CategoryRegionProviderPriorityForm) => {
    return askChangeReason().pipe(
      switchMap(changeNote => RegionApi.saveProviderPriorities(data, changeNote)),
      handleMessage({
        type: requestMessage('update_region_priority'),
      }),
    );
  };
}
